import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, MapPin, LogIn, UserPlus, LogOut, User, Package, Settings, ChevronDown, Home, MessageSquare } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { hasRole, logOut } from '../services/auth';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const userMenuRef = useRef(null);
  
  // Check if we're on the marketplace home (root path)
  const isMarketplace = location.pathname === '/';

  const handleLogout = async () => {
    try {
      await logOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  // Client navigation items
  const clientNavItems = [
    { path: '/client/home', label: 'Home', icon: Home },
    { path: '/available-listings', label: 'Explore', icon: MapPin },
    { path: '/client/bookings', label: 'My Trips', icon: Package },
    { path: '/client/messages', label: 'Messages', icon: MessageSquare },
  ];

  // Get role-specific menu items for dropdown
  const getRoleMenuItems = () => {
    if (hasRole(user, 'client')) {
      return [
        { path: '/client/profile', label: 'Profile', icon: User },
        { path: '/client/home', label: 'Home', icon: Home },
        { path: '/available-listings', label: 'Explore', icon: MapPin },
        { path: '/client/bookings', label: 'My Trips', icon: Package },
        { path: '/settings', label: 'Settings', icon: Settings },
      ];
    }
    return [];
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  const roleMenuItems = getRoleMenuItems();
  const isClient = hasRole(user, 'client');

  return (
    <header className="border-b border-emerald-100 sticky top-0 bg-white/80 backdrop-blur-md z-40">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 md:px-8">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link 
              to="/"
              onClick={() => {
                setIsMenuOpen(false);
              }}
              className="flex items-center space-x-2 sm:space-x-3"
            >
              <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center shadow-md overflow-hidden">
                <img 
                  src="/logo192.png" 
                  alt="Spirara Logo" 
                  className="w-full h-full object-cover scale-110"
                />
              </div>
              <span className="text-xl sm:text-2xl font-light text-gray-900">
                Spirara<span className="text-emerald-700">.</span>
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1 lg:space-x-2">
            {!user && (
              <Link
                to="/about"
                className={`px-4 py-2 rounded-lg text-sm lg:text-base font-medium transition-colors duration-200 ${
                  isActive('/about')
                    ? 'text-emerald-700 bg-emerald-50'
                    : 'text-gray-700 hover:text-emerald-700 hover:bg-emerald-50/50'
                }`}
              >
                About Us
              </Link>
            )}
            {user && isClient ? (
              // Show navigation items for authenticated clients
              clientNavItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`px-4 py-2 rounded-lg text-sm lg:text-base font-medium transition-colors duration-200 flex items-center gap-2 ${
                      isActive(item.path)
                        ? 'text-emerald-700 bg-emerald-50'
                        : 'text-gray-700 hover:text-emerald-700 hover:bg-emerald-50/50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                );
              })
            ) : !user ? (
              // Show Become a Host link for unauthenticated users
              <a
                href={process.env.REACT_APP_HOST_APP_URL || 'https://host.gathur.travel'}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 rounded-lg text-sm lg:text-base font-medium transition-colors duration-200 flex items-center gap-2 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50/50"
              >
                <MapPin className="w-4 h-4" />
                Become a Host
              </a>
            ) : null}
          </nav>

          {/* Desktop CTA / User Menu */}
          <div className="hidden md:flex items-center gap-3 ml-4 lg:ml-6">
            {user ? (
              // Show user menu for authenticated users
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50/50 hover:bg-emerald-50 transition-colors"
                >
                  {user?.photoURL ? (
                    <img 
                      src={user.photoURL} 
                      alt={user.displayName || 'User'} 
                      className="w-8 h-8 rounded-full"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white text-sm font-medium">
                      {(user?.displayName || user?.email || 'U')[0].toUpperCase()}
                    </div>
                  )}
                  <span className="text-sm text-gray-700 font-medium">
                    {user?.displayName || user?.email?.split('@')[0] || 'User'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    {roleMenuItems.map((item) => {
                      const Icon = item.icon;
                      const showDivider = item.label === 'Settings';
                      return (
                        <div key={item.path}>
                          {showDivider && <div className="border-t border-gray-200 my-2"></div>}
                          <Link
                            to={item.path}
                            onClick={() => setIsUserMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                          >
                            <Icon className="w-4 h-4" />
                            {item.label}
                          </Link>
                        </div>
                      );
                    })}
                    <div className="border-t border-gray-200 my-2"></div>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              // Show auth buttons for unauthenticated users
              <>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-emerald-700 transition-colors text-sm lg:text-base font-medium"
                >
                  <LogIn className="w-4 h-4" />
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white px-5 lg:px-6 py-2.5 lg:py-3 rounded-lg transition duration-300 shadow-lg hover:shadow-xl font-medium text-sm lg:text-base"
                >
                  <UserPlus className="w-4 h-4" />
                  Sign Up
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-emerald-100 bg-white">
            <nav className="px-4 py-3 space-y-1">
              {!user && (
                <Link
                  to="/about"
                  onClick={() => setIsMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors duration-200 ${
                    isActive('/about')
                      ? 'text-emerald-700 bg-emerald-50'
                      : 'text-gray-700 hover:text-emerald-700 hover:bg-emerald-50/50'
                  }`}
                >
                  About Us
                </Link>
              )}
              {user && isClient ? (
                // Show navigation items for authenticated clients
                <>
                  {clientNavItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsMenuOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors duration-200 ${
                          isActive(item.path)
                            ? 'text-emerald-700 bg-emerald-50'
                            : 'text-gray-700 hover:text-emerald-700 hover:bg-emerald-50/50'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        {item.label}
                      </Link>
                    );
                  })}
                  <div className="border-t border-emerald-100 pt-3 mt-3">
                    <div className="flex items-center gap-3 px-4 py-2 mb-2">
                      {user?.photoURL ? (
                        <img 
                          src={user.photoURL} 
                          alt={user.displayName || 'User'} 
                          className="w-10 h-10 rounded-full"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white font-medium">
                          {(user?.displayName || user?.email || 'U')[0].toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {user?.displayName || 'User'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {user?.email}
                        </p>
                      </div>
                    </div>
                    {roleMenuItems.map((item) => {
                      const Icon = item.icon;
                      const showDivider = item.label === 'Settings';
                      return (
                        <div key={item.path}>
                          {showDivider && <div className="border-t border-emerald-100 my-2"></div>}
                          <Link
                            to={item.path}
                            onClick={() => setIsMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors text-gray-700 hover:text-emerald-700 hover:bg-emerald-50/50"
                          >
                            <Icon className="w-5 h-5" />
                            {item.label}
                          </Link>
                        </div>
                      );
                    })}
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-red-700 hover:bg-red-50/50 transition-colors"
                    >
                      <LogOut className="w-5 h-5" />
                      Sign Out
                    </button>
                  </div>
                </>
              ) : !user ? (
                // Show Become a Host and auth buttons for unauthenticated users
                <>
                  <a
                    href={process.env.REACT_APP_HOST_APP_URL || 'https://host.gathur.travel'}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50/50"
                  >
                    <MapPin className="w-5 h-5" />
                    Become a Host
                  </a>
                  <Link
                    to="/login"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors text-gray-700 hover:text-emerald-700 hover:bg-emerald-50/50"
                  >
                    <LogIn className="w-5 h-5" />
                    Sign In
                  </Link>
                  <Link
                    to="/signup"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors bg-emerald-700 hover:bg-emerald-800 text-white shadow-lg"
                  >
                    <UserPlus className="w-5 h-5" />
                    Sign Up
                  </Link>
                </>
              ) : null}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
